import { useState } from 'react';
import { useForm } from '@tanstack/react-form';
import { PlaylistService } from '@/services/playlist.service';
import type { CreatePlaylistData } from '@/types';
import { useAuth } from '@/hooks/useAuth';
import { requiredValidator } from '@/lib/forms';

interface CreatePlaylistModalProps {
    show: boolean;
    onHide: () => void;
    onCreated: (playlistId: string) => void;
}

export function CreatePlaylistModal({ show, onHide, onCreated }: CreatePlaylistModalProps) {
    const { user } = useAuth();

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const form = useForm({
        defaultValues: {
            name: '',
            description: '',
            isPublic: false,
        },
        onSubmit: async ({ value }) => {
            if (!user) return;

            setError('');
            setIsLoading(true);

            try {
                const newPlaylistData: CreatePlaylistData = {
                    ownerId: user.id,
                    name: value.name,
                    description: value.description,
                    thumbnail: '',
                    isPublic: value.isPublic,
                    collaborators: { [user.id]: true },
                    settings: {
                        autoAdd: true,
                        voteThresholdRemove: -5,
                        voteThresholdTop: 5
                    },
                    metadata: { totalVideos: 0 }
                };

                const created = await PlaylistService.createPlaylist(newPlaylistData);
                onCreated(created.id);
                handleClose();
            } catch (err: any) {
                setError(err.message || 'Failed to create playlist');
            } finally {
                setIsLoading(false);
            }
        },
    });

    const handleClose = () => {
        form.reset();
        setError('');
        onHide();
    };

    return (
        <div className={`modal fade ${show ? 'show d-block' : ''}`} tabIndex={-1} style={{ backgroundColor: show ? 'rgba(0,0,0,0.5)' : 'transparent' }}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Create New Playlist</h5>
                        <button type="button" className="btn-close" onClick={handleClose} aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        {error && <div className="alert alert-danger">{error}</div>}

                        <form
                            id="createPlaylistForm"
                            onSubmit={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                form.handleSubmit();
                            }}
                        >
                            <form.Field
                                name="name"
                                validators={{
                                    onChange: requiredValidator('Name'),
                                }}
                            >
                                {(field) => (
                                    <div className="mb-3">
                                        <label htmlFor="playlistName" className="form-label">Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="playlistName"
                                            value={field.state.value}
                                            onChange={(e) => field.handleChange(e.target.value)}
                                            onBlur={field.handleBlur}
                                            autoFocus
                                            placeholder="e.g., Friday Jams"
                                        />
                                        {field.state.meta.errors.length > 0 && (
                                            <div className="text-danger small mt-1">
                                                {field.state.meta.errors[0]}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </form.Field>

                            <form.Field name="description">
                                {(field) => (
                                    <div className="mb-3">
                                        <label htmlFor="playlistDesc" className="form-label">Description (Optional)</label>
                                        <textarea
                                            className="form-control"
                                            id="playlistDesc"
                                            rows={3}
                                            value={field.state.value}
                                            onChange={(e) => field.handleChange(e.target.value)}
                                            onBlur={field.handleBlur}
                                            placeholder="What's the vibe?"
                                        ></textarea>
                                    </div>
                                )}
                            </form.Field>

                            <form.Field name="isPublic">
                                {(field) => (
                                    <div className="form-check form-switch mb-3">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="publicSwitch"
                                            checked={field.state.value}
                                            onChange={(e) => field.handleChange(e.target.checked)}
                                        />
                                        <label className="form-check-label" htmlFor="publicSwitch">
                                            Public Playlist
                                        </label>
                                        <div className="form-text">Public playlists can be discovered by anyone.</div>
                                    </div>
                                )}
                            </form.Field>
                        </form>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={handleClose}>Cancel</button>
                        <form.Subscribe
                            selector={(state) => [state.values.name]}
                        >
                            {([name]) => (
                                <button
                                    type="submit"
                                    form="createPlaylistForm"
                                    className="btn btn-primary"
                                    disabled={isLoading || !name.trim()}
                                >
                                    {isLoading ? 'Creating...' : 'Create Playlist'}
                                </button>
                            )}
                        </form.Subscribe>
                    </div>
                </div>
            </div>
        </div>
    );
}
